/**
 * fmwvtool-default.skin.ts - Default Skinnr Skin for FMwvTool
 * 
 * This skin centralizes ALL design and configuration settings for FMwvTool.
 * Can be overridden via URL parameters: ?skin=custom-skin&template=my-template
 * 
 * ARCHITECTURE:
 * - Skinnr: Theme/appearance management (colors, fonts, spacing)
 * - Stylr: Component-level styling (buttons, inputs, cards)
 * - Templatr: Configuration templates (plugin presets, workflows)
 */

export const fmwvtoolDefaultSkin = {
  id: 'fmwvtool-default',
  name: 'FMwvTool Default',
  version: '1.0.0',
  description: 'Default skin for FMwvTool with all plugin configurations',
  
  mode: 'light', // 'light' | 'dark' | 'auto'
  
  // CSS Variables (Skinnr format)
  variables: {
    // Primary brand colors
    'color-primary': '#0066cc',
    'color-primary-hover': '#0052a3',
    'color-primary-active': '#003d7a',
    
    // Secondary colors
    'color-secondary': '#6c757d',
    'color-secondary-hover': '#5a6268',
    
    // Semantic colors
    'color-success': '#10b981',
    'color-warning': '#ff9800',
    'color-error': '#ef4444',
    'color-info': '#3b82f6',
    
    // Neutral colors (used by NavigationWrapper and components)
    'color-background': '#ffffff',
    'surface': '#ffffff',  // Used by nav
    'surface-primary': '#1e1e1e',  // Used by settings modal
    'surface-secondary': '#252525',  // Used by settings modal
    'surface-tertiary': '#2a2a2a',
    'surface-hover': '#2a2a2a',
    'color-surface': '#f8f9fa',
    'bg-hover': 'rgba(0, 0, 0, 0.05)',  // Used by nav buttons
    'color-border': '#e0e0e0',
    'border': '#e2e8f0',  // Used by nav
    'border-primary': '#333',  // Used by settings modal
    'border-secondary': '#2a2a2a',
    'color-text-primary': '#212529',
    'text-primary': '#212529',  // Used by nav and settings
    'text-secondary': '#aaa',  // Used by settings
    'text-tertiary': '#999',
    'color-text-secondary': '#6c757d',
    'color-text-disabled': '#adb5bd',
    
    // Accent colors (used by settings modal)
    'accent-primary': '#0078d4',
    'accent-primary-hover': '#006cbe',
    'accent-secondary': '#ff9800',
    'accent-warning': '#ff9800',
    'accent-danger': '#d32f2f',
    'accent-danger-hover': '#b71c1c',
    
    // Typography
    'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    'font-family-mono': '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    'font-size-xs': '11px',
    'font-size-sm': '12px',
    'font-size-base': '14px',
    'font-size-md': '16px',
    'font-size-lg': '18px',
    'font-size-xl': '20px',
    'font-weight-normal': '400',
    'font-weight-medium': '500',
    'font-weight-semibold': '600',
    'font-weight-bold': '700',
    'line-height-tight': '1.25',
    'line-height-normal': '1.5',
    'line-height-relaxed': '1.75',
    
    // Spacing
    'spacing-xs': '4px',
    'spacing-sm': '8px',
    'spacing-md': '12px',
    'spacing-lg': '16px',
    'spacing-xl': '24px',
    'spacing-2xl': '32px',
    'spacing-3xl': '48px',
    
    // Border radius
    'border-radius-sm': '4px',
    'border-radius-md': '6px',
    'border-radius-lg': '8px',
    'border-radius-xl': '12px',
    'border-radius-full': '9999px',
    
    // Shadows
    'shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.05)',
    'shadow-md': '0 2px 8px rgba(0, 0, 0, 0.1)',
    'shadow-lg': '0 4px 12px rgba(0, 0, 0, 0.15)',
    'shadow-xl': '0 8px 24px rgba(0, 0, 0, 0.2)',
    
    // Transitions
    'transition-fast': '150ms',
    'transition-normal': '250ms',
    'transition-slow': '350ms',
  },
  
  // ============================================================================
  // PLUGIN CONFIGURATIONS
  // ============================================================================
  plugins: {
    // Outliner Plugin
    outliner: {
      appearance: {
        defaultView: 'single', // 'single' | 'multi'
        showLineNumbers: false,
        indentSize: 24,
        nodeHeight: 32,
        fontSize: '14px',
        fontFamily: 'inherit',
      },
      
      colors: {
        nodeBg: 'transparent',
        nodeHoverBg: '#f5f5f5',
        nodeSelectedBg: '#e3f2fd',
        nodeText: 'inherit',
        nodeBorder: '#e0e0e0',
        expandIcon: '#666',
      },
      
      behavior: {
        autoSave: false,
        saveMode: 'batch', // 'auto' | 'batch'
        expandOnLoad: true,
        selectOnCreate: true,
        focusOnEdit: true,
      },
      
      toolbar: {
        showFieldViewToggle: true,
        showImportExport: true,
        showNodeCount: true,
        position: 'top', // 'top' | 'bottom'
      },
      
      multiField: {
        defaultFields: [
          { id: 'task', label: 'Task', type: 'text', width: 300 },
          { id: 'status', label: 'Status', type: 'select', width: 120, options: ['Not Started', 'In Progress', 'Done', 'Blocked'] },
          { id: 'priority', label: 'Priority', type: 'select', width: 100, options: ['Low', 'Medium', 'High', 'Critical'] },
          { id: 'dueDate', label: 'Due Date', type: 'date', width: 150 },
        ],
        resizableColumns: true,
        reorderableColumns: true,
      }
    },
    
    // PDF Viewer Plugin
    pdfViewer: {
      appearance: {
        defaultZoom: 'fit-width', // 'fit-width' | 'fit-page' | number
        showToolbar: true,
        showSidebar: false,
        theme: 'light', // 'light' | 'dark'
      },
      
      toolbar: {
        showZoomControls: true,
        showPageNav: true,
        showDownload: true,
        showPrint: true,
        showSearch: true,
      },
      
      behavior: {
        enableTextSelection: true,
        enableAnnotations: false,
        rememberPage: true,
      }
    },
    
    // Picker Plugin
    picker: {
      appearance: {
        layout: 'grid', // 'grid' | 'list'
        itemsPerRow: 4,
        showThumbnails: true,
        showLabels: true,
        cardStyle: 'elevated', // 'flat' | 'elevated' | 'outlined'
      },
      
      colors: {
        cardBg: '#ffffff',
        cardHoverBg: '#f8f9fa',
        cardSelectedBg: '#e3f2fd',
        cardBorder: '#e0e0e0',
      },
      
      behavior: {
        multiSelect: false,
        searchEnabled: true,
        filterEnabled: true,
        sortEnabled: true,
      }
    },
    
    // Field Matcher Plugin
    fieldMatcher: {
      appearance: {
        showPreview: true,
        showConfidence: true,
        highlightMatches: true,
      },
      
      matching: {
        algorithm: 'fuzzy', // 'exact' | 'fuzzy' | 'semantic'
        threshold: 0.7,
        caseSensitive: false,
      },
      
      ui: {
        showSuggestions: true,
        autoMap: false,
        confirmBeforeApply: true,
      }
    },
    
    // Tag Manager Plugin
    tagManager: {
      appearance: {
        tagStyle: 'pill', // 'pill' | 'square' | 'minimal'
        colorScheme: 'auto', // 'auto' | 'manual'
        showCount: true,
      },
      
      behavior: {
        allowCreate: true,
        allowDelete: true,
        allowRename: true,
        caseSensitive: false,
      },
      
      ui: {
        showSearch: true,
        showFilter: true,
        showSort: true,
        groupBy: 'none', // 'none' | 'category' | 'color'
      }
    }
  },
  
  // ============================================================================
  // DATA SUBMISSION SETTINGS
  // ============================================================================
  dataSubmission: {
    defaultMethod: 'script', // 'script' | 'odata' | 'dapi'
    defaultSaveMode: 'batch', // 'auto' | 'batch'
    
    fileMakerScript: {
      defaultScriptName: 'jsRunScript',
      timeout: 5000,
      retryAttempts: 3,
      showErrors: true,
    },
    
    odata: {
      defaultEndpoint: '',
      authType: 'basic', // 'basic' | 'oauth' | 'token'
      timeout: 10000,
      retryAttempts: 3,
    },
    
    dapi: {
      defaultEndpoint: '',
      authType: 'token',
      timeout: 10000,
      retryAttempts: 3,
    },
    
    logging: {
      enabled: true,
      level: 'info', // 'debug' | 'info' | 'warn' | 'error'
      showInConsole: true,
      persistLogs: false,
    }
  },
  
  // ============================================================================
  // NAVIGATION & LAYOUT
  // ============================================================================
  navigation: {
    defaultState: 'full', // 'full' | 'collapsed' | 'hidden'
    showSidebar: true,
    showToolbar: true,
    showBreadcrumbs: true,
    
    customizeButton: {
      show: false,
      position: 'top-right', // 'top-right' | 'bottom-right' | 'floating'
      icon: '🎨',
    },
    
    favorites: {
      enabled: true,
      maxItems: 10,
      showInNav: true,
    },
    
    // Navigation Menu Styling (Selectr-based)
    menu: {
      mode: 'list' as const, // 'dropdown' | 'list' | 'menu' | 'modal'
      searchable: true,
      grouped: true,
      showIcons: true,
      showDescriptions: true,
      
      // Filtr configuration
      filter: {
        fuzzySearch: true,
        searchFields: ['label', 'description', 'tags', 'category'],
        highlightMatches: true,
        minScore: 0.3,
        caseSensitive: false,
      },
      
      // Context selector
      contexts: {
        enabled: true,
        showSelector: true,
        defaultContext: 'fmwvtool-all',
        allowUserContexts: true,
        allowDynamicContexts: true,
      },
      
      // Styling
      styles: {
        width: 280,
        maxHeight: 600,
        itemHeight: 40,
        groupHeaderHeight: 32,
        
        // Colors (use CSS variables)
        background: 'var(--surface-primary)',
        text: 'var(--text-primary)',
        textSecondary: 'var(--text-secondary)',
        border: 'var(--border-primary)',
        hover: 'var(--surface-hover)',
        active: 'var(--accent-primary)',
        groupHeader: 'var(--surface-secondary)',
        
        // Search
        searchBackground: 'var(--surface-secondary)',
        searchBorder: 'var(--border-secondary)',
        searchPlaceholder: 'var(--text-tertiary)',
        
        // Highlights
        matchHighlight: 'var(--accent-secondary)',
        currentItem: 'var(--accent-primary)',
        
        // Icons
        iconSize: 20,
        iconOpacity: 0.8,
        
        // Spacing
        padding: 12,
        gap: 8,
        borderRadius: 8,
      }
    },
    
    // Context Selector Styling
    contextSelector: {
      position: 'top' as const, // 'top' | 'bottom' | 'floating'
      showInSidebar: true,
      
      styles: {
        background: 'var(--surface-secondary)',
        border: 'var(--border-primary)',
        text: 'var(--text-primary)',
        hover: 'var(--surface-hover)',
        active: 'var(--accent-primary)',
        padding: 12,
        borderRadius: 8,
      }
    }
  },
  
  // ============================================================================
  // SAMPLES & DEVELOPMENT
  // ============================================================================
  development: {
    samplesEnabled: true,
    showDebugInfo: false,
    verboseLogging: false,
    
    sampleData: {
      outliner: {
        nodes: [
          { id: 'root-1', text: 'Welcome to Outliner', parentId: null, generation: 0, familyId: 'family-1', expanded: true, children: ['child-1', 'child-2'] },
          { id: 'child-1', text: 'Press Enter to create a new sibling', parentId: 'root-1', generation: 1, familyId: 'family-1', children: [] },
          { id: 'child-2', text: 'Press Tab to indent, Shift+Tab to outdent', parentId: 'root-1', generation: 1, familyId: 'family-1', children: [] }
        ]
      }
    }
  },
  
  // ============================================================================
  // ACCESSIBILITY
  // ============================================================================
  accessibility: {
    highContrast: false,
    reducedMotion: false,
    focusIndicators: true,
    keyboardNav: true,
    screenReaderOptimized: true,
  },
  
  // ============================================================================
  // PERFORMANCE
  // ============================================================================
  performance: {
    lazyLoadPlugins: true,
    virtualScrolling: true,
    debounceDelay: 300,
    throttleDelay: 100,
    cacheEnabled: true,
    cacheTTL: 3600000, // 1 hour in ms
  }
};

export default fmwvtoolDefaultSkin;
